import type { CSSProperties, Dispatch } from "react"

import type { SegmentAnalysisAction } from "../hooks/use-segment-analysis"
import type { AddressableLine, Segment } from "../types"

import { ChevronRight, Star } from "@/components/icons"
import { cn } from "@/lib/utils"

import { CountUp } from "./count-up"
import { marketColors } from "../data/mock-segment-analysis-data"
import { DEFAULT_ADDRESSABLE_LINES } from "../types"
import { segmentAddressableTotal } from "../utils/segment-analysis-utils"

type SegmentCardProps = {
  addressableLines?: AddressableLine[]
  /** User-selected stat ids (#2069 B2); absent → AM / CAGR / Share. */
  cardStats?: string[]
  dispatch: Dispatch<SegmentAnalysisAction>
  index?: number
  isActive: boolean
  isFavourite?: boolean
  isOwn?: boolean
  pendingCount?: number
  onToggleFavourite?: () => void
  segment: Segment
}

function shareVsAm(segment: Segment): number {
  const am = segmentAddressableTotal(segment)
  if (am > 0) return Math.round((segment.sales / am) * 1000) / 10
  return segment.share
}

export function SegmentCard({
  addressableLines = DEFAULT_ADDRESSABLE_LINES,
  cardStats,
  dispatch,
  index = 0,
  isActive,
  isFavourite = false,
  isOwn = false,
  pendingCount = 0,
  onToggleFavourite,
  segment,
}: SegmentCardProps) {
  const color = marketColors[segment.marketName] ?? "#94A3B8"
  const style = { "--segment-index": index } as CSSProperties
  void addressableLines

  const am = segmentAddressableTotal(segment)
  const share = shareVsAm(segment)
  const statById: Record<string, { label: string; value: string }> = {
    // MEUR convention (#3295 Nolato feedback #30): the unit goes in the label,
    // matching the sibling "Sales MEUR" / "Market MEUR" tiles below.
    addressable: { label: "Addr. market MEUR", value: `${am}` },
    cagr: { label: "CAGR 5y", value: `${segment.cagr}%` },
    share: { label: "Share %", value: `${share}%` },
    sales: { label: "Sales MEUR", value: `${segment.sales}` },
    size: { label: "Market MEUR", value: `${segment.size}` },
  }
  const chosen = (cardStats ?? []).map((id) => statById[id]).filter(Boolean)
  const classicStats = [
    { label: "Addr. market MEUR", value: `${am}` },
    { label: "CAGR 5y", value: `${segment.cagr}%` },
    { label: "Share %", value: `${share}%` },
  ]
  const stats = chosen.length > 0 ? chosen : classicStats

  return (
    <div
      data-testid="segment-card"
      role="presentation"
      style={style}
      className={cn(
        "segment-card-enter card-interactive group bg-card relative h-full rounded-xl border p-4 text-left shadow-sm",
        isActive
          ? "border-primary/40 ring-primary/15 ring-2"
          : "border-border hover:border-border/80"
      )}
      // Mouse convenience for the whole card; keyboard users activate the inner button.
      onClick={() => dispatch({ type: "selectSegment", segmentId: segment.id, toggle: true })}
    >
      {onToggleFavourite ? (
        <button
          aria-pressed={isFavourite}
          type="button"
          aria-label={
            isFavourite
              ? `Remove from favourites: ${segment.name}`
              : `Add to favourites: ${segment.name}`
          }
          className={cn(
            "focus-visible:ring-ring absolute top-2 right-2 z-10 inline-flex size-7 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:outline-none",
            isFavourite
              ? "text-warning-ink"
              : "text-muted-foreground/50 hover:bg-accent hover:text-warning-ink"
          )}
          onClick={(event) => {
            event.stopPropagation()
            onToggleFavourite()
          }}
        >
          <Star className={cn("size-4", isFavourite && "fill-current")} />
        </button>
      ) : null}
      <button
        className="focus-visible:ring-ring grid h-full w-full gap-4 rounded-md text-left focus-visible:ring-2 focus-visible:outline-none"
        type="button"
      >
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="text-foreground line-clamp-2 text-sm font-semibold">
              {segment.name}
            </span>
            <span className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
              {segment.owner}
              {isOwn ? (
                <span className="bg-primary/10 text-primary rounded-md px-1.5 py-0.5 text-[10px] font-semibold">
                  You
                </span>
              ) : null}
            </span>
          </span>
          <span className={cn("flex shrink-0 items-center gap-1.5", onToggleFavourite && "pr-7")}>
            {pendingCount > 0 ? (
              <span
                className="bg-warning/15 text-warning-foreground inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold"
                title={`${pendingCount} change${pendingCount === 1 ? "" : "s"} awaiting approval`}
              >
                {pendingCount} pending
              </span>
            ) : null}
            <span
              className="rounded-md px-2 py-1 text-xs font-medium"
              style={{ background: `${color}1f`, color }}
            >
              {segment.marketName.split(" ")[0]}
            </span>
          </span>
        </span>
        <span
          className={cn(
            "text-muted-foreground grid gap-2 text-xs",
            stats.length === 4 ? "grid-cols-2" : "grid-cols-3"
          )}
        >
          {stats.map((stat) => (
            <span key={stat.label}>
              <strong className="text-foreground block text-base tabular-nums">
                <CountUp value={stat.value} />
              </strong>
              {stat.label}
            </span>
          ))}
        </span>
        <span className="flex items-center justify-end gap-3">
          <span className="text-muted-foreground group-hover:text-foreground inline-flex items-center gap-1 text-xs font-medium transition">
            Open
            <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </span>
      </button>
    </div>
  )
}
